import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Lightning, Copy, TrendUp, TrendDown } from "@phosphor-icons/react";
import { BrandLogo } from "../components/BrandLogo";
import { API } from "../lib/api";
import useSeo from "../lib/useSeo";

function money(n, sign = false) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Number(n);
  const a = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  return (sign && v >= 0 ? "+$" : v < 0 ? "−$" : "$") + a;
}

export default function SharedPropChallenge() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useSeo({
    title: d ? `${d.trader_name || d.owner_name} · ${d.firm_name} Challenge · Tradelog` : "Prop Firm Challenge · Tradelog",
    description: d
      ? `${d.trader_name || d.owner_name} ${d.status === 'passed' ? 'PASSED' : 'is taking on'} the ${d.firm_name} ${d.challenge_label} challenge on Tradelog — ${d.pct_return}% return over ${d.days_traded} trading day(s).`
      : "A shared Tradelog Prop Firm Challenge — see the trader's run under real prop-firm rules.",
    canonical: `/prop-challenge/share/${token}`,
  });
  useEffect(() => {
    document.documentElement.classList.add("dark");
    axios.get(`${API}/prop-sim/shared/${token}`)
      .then(r => setD(r.data))
      .catch(() => setErr("Share not found or revoked."));
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center bg-neutral-950 text-neutral-500">{err}</div>;
  if (!d) return <div className="min-h-screen grid place-items-center bg-neutral-950 text-neutral-500">Loading…</div>;

  const positive = d.total_pnl >= 0;
  const passed = d.status === "passed";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* noop */ }
  };

  const targetHit = d.pct_return >= d.target_pct;

  return (
    <div className="min-h-screen bg-neutral-950 text-white grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="font-display text-xl font-bold">Tradelog</span>
          </div>
          <a href="/" className="text-sm text-neutral-400 hover:text-white">Try the Prop Firm Challenge →</a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="relative rounded-2xl overflow-hidden border border-white/10"
          style={{
            background:
              "radial-gradient(120% 120% at 100% 0%, rgba(16,185,129,0.10) 0%, transparent 50%)," +
              "radial-gradient(120% 120% at 0% 100%, rgba(59,130,246,0.08) 0%, transparent 50%)," +
              "linear-gradient(145deg, #0a0a0a 0%, #050505 100%)",
          }}>
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
                "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }} />
          <div className="relative p-8 sm:p-10">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-3">
                <BrandLogo size={22} wordmark={false} />
                <div>
                  <p className="font-display text-lg font-bold leading-none">Tradelog Prop Firm Challenge</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-400 mt-1">{d.firm_name} · {d.challenge_label}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full font-display text-xs font-black tracking-wide ${
                passed ? "bg-emerald-500 text-black"
                : targetHit ? "bg-blue-500 text-white"
                : positive ? "bg-white/10 text-white" : "bg-red-500 text-white"
              }`} data-testid="ss-pc-status">
                {passed ? "PASSED" : targetHit ? "ON TRACK" : positive ? "LIVE" : "UNDERWATER"}
              </span>
            </div>

            <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Net P&amp;L</p>
            <p className={`font-display font-black leading-none tracking-tight text-5xl sm:text-6xl ${positive ? "text-emerald-400" : "text-red-400"}`} data-testid="ss-pc-pnl">
              {money(d.total_pnl, true)}
            </p>
            <p className="text-neutral-400 text-xs mt-3 font-mono uppercase tracking-widest">
              on ${d.starting_balance.toLocaleString()} · {d.pct_return}% return · {d.trader_name || d.owner_name}{d.handle ? ` · @${d.handle}` : ""}
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Return</p>
                <p className={`font-mono-num font-bold text-lg ${positive ? "text-emerald-400" : "text-red-400"}`}>{d.pct_return}%</p>
                <p className="text-[10px] text-neutral-500">target {d.target_pct}%</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Equity</p>
                <p className="font-mono-num font-bold text-lg">{money(d.current_equity)}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Max DD</p>
                <p className="font-mono-num font-bold text-lg">{d.max_dd_pct}%</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Days</p>
                <p className="font-mono-num font-bold text-lg">{d.days_traded}/{d.min_days}</p>
              </div>
            </div>

            <div className="mt-8 flex items-end justify-between gap-4 flex-wrap">
              <button onClick={copyLink} className="h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 flex items-center gap-2 text-xs" data-testid="ss-pc-copy">
                <Copy size={14} /> Copy link
              </button>
              <div className="p-2 bg-white rounded-lg shadow-2xl" data-testid="ss-pc-qr">
                <QRCodeSVG value={shareUrl} size={72} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
            </div>
          </div>
        </div>

        {d.closed_trades?.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-3">Recent trades on this challenge</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {d.closed_trades.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
                  <div>
                    <p className="text-sm font-medium">{t.symbol}</p>
                    <p className="text-[10px] text-neutral-500">{t.date} · {(t.direction || "").toUpperCase()}</p>
                  </div>
                  <p className={`font-mono-num font-bold text-sm ${(t.pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {money(t.pnl, true)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className="text-center text-xs text-neutral-600 pt-4 pb-8">
          Shared by {d.owner_name} · {(d.created_at || "").slice(0, 10)}
        </footer>
      </main>
    </div>
  );
}
